import { setSessionLostHandler, useSession } from "@wirevod/app-core";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";

function nextParam(pathname: string, search: string): string {
  const target = `${pathname}${search}`;
  return target === "/" ? "" : `?next=${encodeURIComponent(target)}`;
}

/** Protected area: signed-in users only; a forced password change comes first. */
export function RequireSession() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setSessionLostHandler(() => {
      navigate(`/login${nextParam(window.location.pathname, window.location.search)}`, {
        replace: true,
      });
    });
  }, [navigate]);

  if (!session) {
    return <Navigate to={`/login${nextParam(location.pathname, location.search)}`} replace />;
  }
  if (session.claims.pwd_change && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (!session.claims.pwd_change && location.pathname === "/change-password") {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

/** Sign-in pages: already signed-in users go straight to the app. */
export function RedirectIfSignedIn() {
  const session = useSession();
  const location = useLocation();
  if (session) {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={safeNext(next)} replace />;
  }
  return <Outlet />;
}

/** Only same-app paths are allowed as a post-login destination. */
export function safeNext(next: string | null): string {
  if (!next?.startsWith("/") || next.startsWith("//")) return "/";
  return next;
}
